/**
 * Reports — By guest · By campaign · Revenue. Port of screens-reports-money.jsx.
 *
 * Data source (kit drop 2026-07-09, voyage #752): each tab calls ITS dedicated
 * reports-money op — getReportGuests / getReportCampaigns / getReportRevenue,
 * plus getReportGoals for pacing — never the Dashboard op, and never a derived
 * proportional split. Every figure on these screens is a real contract field;
 * when a field is genuinely absent (cost tracking off, promo not tracked, no
 * prior period) the UI renders "—" or an explanatory sub-line, per the
 * honest-rail doctrine. Money fields arrive in minor units → divided by 100 once.
 */
import { useEffect, useMemo, useState } from "react";
const AVATAR_GLYPH_RATIO = 0.38; // ui-config — initials glyph scales with avatar diameter (presentational ratio, not data)
import React from "react";
import { Btn, Card, Icon, Pill, T, tx } from "@v0y4ge/jrni-ui";
import { Screen, PageHeader } from "../../shell/chrome";
import { toast } from "../../shell/toast";
import { useNav } from "../../shell/router";
import * as apiClient from "@v0y4ge/api-client";
import { getReportGuests, getReportRevenue, getReportCampaigns, reportsMoney, listLocations } from "@v0y4ge/api-client";
import { LOCATIONS_OPTS, REPORTS_MONEY_OPTS, REPORTS_OVERVIEW_OPTS } from "../../shell/request-opts";
import {
  RANGES, RangeSelect, LocationSelect, reportTabs, ScoreTile, delta,
  fmtNum, fmtMoney, fmtMoneyFull, fmtPct,
} from "./ReportsOverview";
import { useT, t as i18nT } from "../../lib/i18n";
import { useCustomersVocab } from "../customers/customers-config";

const thStyle = { padding: "10px 16px", textAlign: "left" as const, fontWeight: 600, whiteSpace: "nowrap" as const };
const tdStyle = { padding: "12px 16px", verticalAlign: "middle" as const, color: T.n2 };

/* ── Shared: horizontal breakdown bars ───────────────────────────────── */
export function BreakdownBars({ rows, valueKey, money, footer }: {
  rows: { id: string; label: string; color: string; [k: string]: any }[]; valueKey: string; money?: boolean; footer?: React.ReactNode;
}) {
  const t = useT();
  const max = Math.max(1, ...rows.map((r) => r[valueKey] || 0));
  return (
    <div style={{ padding: "14px 16px", display: "flex", flexDirection: "column", gap: 10 }}>
      {rows.length === 0 ? <div style={{ fontSize: 12.5, color: T.soft, padding: "8px 0" }}>{t("reports.extra.noDataForThisPeriod", "No data for this period.")}</div> : rows.map((r) => (
        <div key={r.id} style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12.5 }}>
            <span style={{ color: T.n1, fontWeight: 600, display: "inline-flex", alignItems: "center", gap: 7 }}><span style={{ width: 9, height: 9, borderRadius: 3, background: r.color }} />{r.label}</span>
            <span style={{ color: T.soft, fontVariantNumeric: "tabular-nums" }}>{money ? fmtMoneyFull(r[valueKey] || 0) : fmtNum(r[valueKey] || 0)}</span>
          </div>
          <div style={{ height: 6, background: T.n5, borderRadius: 999, overflow: "hidden" }}>
            <div style={{ height: "100%", width: `${((r[valueKey] || 0) / max) * 100}%`, background: r.color, borderRadius: 999 }} />
          </div>
        </div>
      ))}
      {footer ? <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11.5, color: T.soft, borderTop: `1px solid ${T.border}`, paddingTop: 9, marginTop: 2 }}>{footer}</div> : null}
    </div>
  );
}

/* ── Shared: real CSV export ─────────────────────────────────────────── */
export function ReportExport({ filename, rows }: { filename: string; rows: () => (string | number)[][] }) {
  const t = useT();
  const doExport = () => {
    const csv = rows().map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    a.download = i18nT("reports.extra.filenameCsv.icu", { value0: filename });
    a.click();
    toast({ tone: "success", title: t("reports.extra.exportDownloaded", "Export downloaded"), subtitle: i18nT("reports.extra.filenameCsv.icu", { value0: filename }) });
  };
  return <Btn variant="secondary" icon={<Icon.download size={14} />} onClick={doExport}>{t("common.actions.export", "Export")}</Btn>;
}

/* Small avatar for leaderboards. */
function RepAvatar({ initials, color, size = 28 }: { initials: string; color: string; size?: number }) {
  return <span style={{ width: size, height: size, borderRadius: 999, background: color, color: T.white, display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: size * AVATAR_GLYPH_RATIO, fontWeight: 700, flexShrink: 0 }}>{initials}</span>;
}

/* ── Shared data plumbing ────────────────────────────────────────────── */
/** Minor currency units → major. */
const m$ = (minor: number | null | undefined) => (minor ?? 0) / 100;
const PALETTE = [T.tPurple, T.tPink, T.tLightBlue, T.tDarkBlue, T.tOrange]; // ui-config — avatar/bar hues
const TIER_COLORS = ["#5142C7", "#0E7490", "#C77F14", "#8955F2", "#C13584"]; // ui-config
const MONTHS_S = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"]; // ui-config
const initials = (name: string) => name.split(" ").map((p) => p[0]).join("").toUpperCase().slice(0, 2);
const fmtDay = (iso: string | null | undefined) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? String(iso) : `${d.getDate()} ${MONTHS_S[d.getMonth()]} ${d.getFullYear()}`;
};

/** Fetch one reports-money aggregate for the tab's range/location filters. */
function useMoneyReport<TRes>(fetchOp: (params: any, opts: typeof REPORTS_MONEY_OPTS) => Promise<TRes>, range: string, location: string) {
  const [data, setData] = useState<TRes | null>(null);
  const [failed, setFailed] = useState(false);
  const [locs, setLocs] = useState<{ id: string; name: string }[]>([]);
  useEffect(() => { listLocations({}, LOCATIONS_OPTS).then((r: any) => { const arr = r.locations ?? r.data ?? []; setLocs(arr.map((l: any) => ({ id: l.id, name: l.name ?? l.locationName ?? "Location" }))); }).catch(() => {}); }, []);
  useEffect(() => {
    let live = true;
    setFailed(false);
    fetchOp({ range, ...(location === "all" ? {} : { locations: location }) }, REPORTS_MONEY_OPTS)
      .then((res) => { if (live) setData(res); })
      .catch(() => { if (live) { setData(null); setFailed(true); } });
    return () => { live = false; };
  }, [fetchOp, range, location]);
  return { data, failed, locs };
}

/** Real goal pacing for a range — getReportGoals().pacing (revenue entries in minor units). */
function useGoalPacing(range: string) {
  const [pacing, setPacing] = useState<any[]>([]);
  useEffect(() => {
    let live = true;
    reportsMoney.getReportGoals({ range } as any, REPORTS_MONEY_OPTS)
      .then((res: any) => { if (live) setPacing(res?.pacing ?? []); })
      .catch(() => { if (live) setPacing([]); });
    return () => { live = false; };
  }, [range]);
  return pacing;
}

function LoadErrorNote({ failed }: { failed: boolean }) {
  const t = useT();
  if (!failed) return null;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 14px", borderRadius: 8, border: `1px solid ${T.border}`, background: T.white, color: T.red1, fontSize: 13 }}>
      <Icon.warn size={14} /> {t("reports.extra.couldnTLoadThisReportRetryInAMoment", "Couldn't load this report. Retry in a moment.")}</div>
  );
}

/* ═══ By guest ═══════════════════════════════════════════════════════ */
export function ReportsByGuest() {
  const t = useT();
  const nav = useNav();
  /* Nouns come from the tenant’s served vocabulary (2026-08-31): a university
     has students and attendees, not "guests". Lifetime value is a retail/bank
     question — it renders only when the tenant actually reports one, so a campus
     is not asked to read a column of dashes. */
  const vocab = useCustomersVocab();
  const noun = (vocab?.entity ?? "attendee").toLowerCase();
  const nounPlural = (vocab?.entityPlural ?? "attendees").toLowerCase();
  const Noun = noun.charAt(0).toUpperCase() + noun.slice(1);
  const [range, setRange] = useState("12m");
  const [location, setLocation] = useState("all");
  const { data, failed, locs } = useMoneyReport(getReportGuests, range, location);
  const rangeMeta = RANGES.find((r) => r.id === range) ?? RANGES[4];
  const locName = location === "all" ? "all locations" : (locs.find((l) => l.id === location)?.name ?? "all locations");

  const sum = (data as any)?.summary ?? null;
  const cmp = (data as any)?.comparison ?? null;
  /* Lifetime value is only shown when the tenant reports one — a campus with no
     paid events would otherwise read a tile and a column of dashes. */
  const ltvTracked = !!sum && Number(sum.averageLifetimeValueMinor) > 0;
  const dActive = sum && cmp?.activeGuests != null ? delta(sum.activeGuests, cmp.activeGuests) : undefined;
  const dNew = sum && cmp?.newGuests != null ? delta(sum.newGuests, cmp.newGuests) : undefined;

  const byTier = ((data as any)?.segments?.byTicketTier ?? []).map((t: any, i: number) => ({
    id: t.id ?? t.label, label: t.label, color: t.color ?? TIER_COLORS[i % TIER_COLORS.length], reg: t.reg ?? t.count ?? 0,
  }));
  const segs = ((data as any)?.segments?.bySegment ?? []).slice(0, 6).map((sg: any, i: number) => ({
    id: sg.label, label: sg.label, color: PALETTE[i % PALETTE.length], reg: sg.count ?? 0,
  }));
  const top = ((data as any)?.topGuests ?? []).slice(0, 6).map((g: any, i: number) => ({
    id: g.guestId ?? null, init: initials(g.guestName ?? ""), color: PALETTE[i % PALETTE.length],
    name: g.guestName, attended: g.eventsAttended ?? 0,
    lv: g.lifetimeValueMinor != null ? m$(g.lifetimeValueMinor) : null,
    last: fmtDay(g.lastSeenAt),
  }));
  const cohort = (data as any)?.cohortRetention ?? null;
  const cohortRows: { label: string; values: (number | null)[] }[] = cohort?.available ? (cohort.uiBuckets ?? []) : [];

  const exportRows = () => {
    const rows: (string | number)[][] = [["Metric", "Value", "Prior period"],
      ["Active guests", sum?.activeGuests ?? "—", cmp?.activeGuests ?? "—"],
      ["New guests", sum?.newGuests ?? "—", cmp?.newGuests ?? "—"],
      ["Repeat attendees", sum?.repeatAttendees ?? "—", ""],
      ["Avg events / guest", sum?.averageEventsPerGuest ?? "—", ""],
      ["Avg lifetime value", sum ? m$(sum.averageLifetimeValueMinor) : "—", ""],
      ["", "", ""], ["Tier", "Registrations", ""]];
    byTier.forEach((t: any) => rows.push([t.label, t.reg, ""]));
    return rows;
  };

  return (
    <Screen rail="reports" tabs={reportTabs(nav, "guests")} activeTab="guests">
      <PageHeader
        title={`By ${noun}`}
        subtitle={`Engagement and segments${ltvTracked ? " & lifetime value" : ""} · ${rangeMeta.label.toLowerCase()} · ${locName}`}
        breadcrumb={<span style={{ cursor: "pointer" }} onClick={() => nav.go("reports")}>{t("reports.extra.reports", "← Reports")}</span>}
        actions={<>
          <LocationSelect value={location} onChange={setLocation} locs={locs} />
          <RangeSelect value={range} onChange={(id) => { setRange(id); toast({ tone: "info", title: i18nT("reports.extra.rangeRangesFindRRIdIdRanges4Label.icu", { value0: (RANGES.find((r) => r.id === id) || RANGES[4]).label }), subtitle: t("reports.extra.guestMetricsUpdated", "Guest metrics updated.") }); }} />
          <ReportExport filename={`guest-report-${range}`} rows={exportRows} />
        </>}
      />
      <div style={{ flex: 1, overflow: "auto", padding: "16px 24px 24px", display: "flex", flexDirection: "column", gap: 16 }}>
        <LoadErrorNote failed={failed} />
        <div style={{ display: "grid", gridTemplateColumns: `repeat(${ltvTracked ? 5 : 4}, 1fr)`, gap: 12 }}>          <ScoreTile label={`Active ${nounPlural}`} value={sum ? fmtNum(sum.activeGuests) : "—"} delta={dActive} accent={T.p1} icon={<Icon.users size={14} />} />
          <ScoreTile label={i18nT("reports.extra.newRangemetaLabel.icu", { value0: rangeMeta.label })} value={sum ? fmtNum(sum.newGuests) : "—"} delta={dNew} accent={T.green1} icon={<Icon.plus size={14} />} />
          <ScoreTile label={t("reports.extra.repeatAttendees", "Repeat attendees")} value={sum ? fmtNum(sum.repeatAttendees) : "—"} accent={T.tDarkBlue} icon={<Icon.refresh size={14} />} sub={sum && sum.attendedEvents > 0 ? `${Math.round(sum.repeatAttendees / sum.attendedEvents * 100)}% of attendees` : undefined} />
          <ScoreTile label={`Avg events / ${noun}`} value={sum ? sum.averageEventsPerGuest.toFixed(1) : "—"} accent={T.tPurple} icon={<Icon.chart size={14} />} />
          {ltvTracked ? <ScoreTile label={t("reports.extra.lifetimeValueAvg", "Lifetime value · avg")} value={sum ? fmtMoney(m$(sum.averageLifetimeValueMinor)) : "—"} accent={T.tOrange} icon={<Icon.ticket size={14} />} /> : null}
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1.3fr 1fr", gap: 16 }}>
          <Card>
            <div style={{ padding: "12px 16px", borderBottom: `1px solid ${T.border}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <h3 style={{ ...tx.h3, fontWeight: 600 }}>{t("reports.extra.topGuestsByEngagement", "Top guests by engagement")}</h3>
              <Btn variant="link" size="sm" onClick={() => nav.go("guests")}>{t("reports.extra.seeAll", "See all →")}</Btn>
            </div>
            {/* ui-raw: hand-rolled table — Table primitive's bordered/radius wrapper, uniform
              * row-hover highlight and sort-icon slots would visibly diverge from this table's
              * conditional per-row border, composite avatar+name cell, and click-to-navigate rows. */}
            <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: T.font, fontSize: 13 }}>
              <thead>
                <tr style={{ background: T.n6, color: T.soft, fontSize: 11, textTransform: "uppercase", fontWeight: 600 }}>
                  <th style={thStyle}>{Noun}</th>
                  <th style={{ ...thStyle, textAlign: "right" }}>{t("reports.extra.events", "Events")}</th>
                  {ltvTracked ? <th style={{ ...thStyle, textAlign: "right" }}>{t("reports.extra.lifetimeValue", "Lifetime value")}</th> : null}
                  <th style={{ ...thStyle, textAlign: "right" }}>{t("reports.extra.lastSeen", "Last seen")}</th>
                </tr>
              </thead>
              <tbody>
                {top.length === 0 ? (
                  <tr><td style={{ ...tdStyle, color: T.soft, fontSize: 12.5 }} colSpan={ltvTracked ? 4 : 3}>{`No ${noun} activity in this period.`}</td></tr>
                ) : top.map((g: any, i: number) => (
                  <tr key={g.id ?? i} onClick={() => { if (g.id) nav.go("guests.profile", { id: g.id }); }} style={{ borderTop: i ? `1px solid ${T.border}` : "none", cursor: g.id ? "pointer" : "default" }}>
                    <td style={tdStyle}>
                      <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                        <RepAvatar initials={g.init} color={g.color} />
                        <span style={{ color: T.n1, fontWeight: 600 }}>{g.name}</span>
                      </div>
                    </td>
                    <td style={{ ...tdStyle, textAlign: "right", color: T.n1, fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>{g.attended}</td>
                    {ltvTracked ? <td style={{ ...tdStyle, textAlign: "right", color: T.n1, fontVariantNumeric: "tabular-nums" }}>{g.lv != null ? fmtMoneyFull(g.lv) : "—"}</td> : null}
                    <td style={{ ...tdStyle, textAlign: "right", color: T.soft, fontSize: 12 }}>{g.last}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>

          <Card>
            <div style={{ padding: "12px 16px", borderBottom: `1px solid ${T.border}` }}>
              <h3 style={{ ...tx.h3, fontWeight: 600 }}>{t("reports.extra.registrationsByTier", "Registrations by tier")}</h3>
            </div>
            <BreakdownBars rows={byTier} valueKey="reg" />
          </Card>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1.3fr", gap: 16 }}>
          <Card>
            <div style={{ padding: "12px 16px", borderBottom: `1px solid ${T.border}` }}>
              <h3 style={{ ...tx.h3, fontWeight: 600 }}>{t("reports.extra.bySegment", "By segment")}</h3>
            </div>
            <BreakdownBars rows={segs} valueKey="reg" />
          </Card>

          <Card>
            <div style={{ padding: "12px 16px", borderBottom: `1px solid ${T.border}` }}>
              <h3 style={{ ...tx.h3, fontWeight: 600 }}>{t("reports.extra.cohortRetention", "Cohort retention")}</h3>
            </div>
            <div style={{ padding: 20 }}>
              <div style={{ fontSize: 12, color: T.soft, marginBottom: 8 }}>{t("reports.extra.guestsWhoReturnedWithin90DaysOfTheirFirstEvent", "Guests who returned within 90 days of their first event")}</div>
              {cohortRows.length === 0 ? (
                <div style={{ fontSize: 12.5, color: T.soft, padding: "8px 0" }}>{t("reports.extra.notEnoughAttendanceHistoryToComputeCohortsYet", "Not enough attendance history to compute cohorts yet.")}</div>
              ) : (
                <div style={{ display: "grid", gridTemplateColumns: "60px repeat(4, 1fr)", gap: 4, fontSize: 11 }}>
                  <div></div>
                  {["1 wk", "1 mo", "2 mo", "3 mo"].map((l) => <div key={l} style={{ textAlign: "center", color: T.soft, fontWeight: 600 }}>{l}</div>)}
                  {cohortRows.map((c) => (
                    <React.Fragment key={c.label}>
                      <div style={{ fontSize: 11, color: T.soft, fontWeight: 600, display: "flex", alignItems: "center" }}>{c.label}</div>
                      {c.values.map((v, j) => (
                        <div key={j} style={{ padding: "6px 4px", textAlign: "center", fontSize: 11, fontWeight: 600,
                          background: v == null ? "transparent" : `rgba(81, 66, 199, ${0.08 + v / 100})`,
                          color: v == null ? T.n3 : v > 30 ? T.white : T.n1, borderRadius: 3 }}>{v == null ? "—" : t('reports.extra.text', { v })}</div>
                      ))}
                    </React.Fragment>
                  ))}
                </div>
              )}
            </div>
          </Card>
        </div>
      </div>
    </Screen>
  );
}

/* ═══ By campaign — attribution report ═══════════════════════════════
 * Campaign → recipients → registrations → attended → revenue, with ROI,
 * cost-per-registration, conversion, and a paid-campaign funnel. Rows and
 * KPIs come straight from getReportCampaigns().byCampaign / .kpis — the
 * response carries costAvailable/nullable cost fields for the "—" cases. */
export function ReportsByCampaign() {
  const t = useT();
  const nav = useNav();
  const [range, setRange] = useState("30d");
  const [location, setLocation] = useState("all");
  const [sortKey, setSortKey] = useState<"revenue" | "reg" | "roi">("revenue");
  const { data, failed, locs } = useMoneyReport(getReportCampaigns, range, location);
  const rangeMeta = RANGES.find((r) => r.id === range) ?? RANGES[1];
  const locName = location === "all" ? "all locations" : (locs.find((l) => l.id === location)?.name ?? "all locations");

  const byCampaign = useMemo(() => ((data as any)?.byCampaign ?? []).map((c: any, i: number) => ({
    id: c.id, name: c.name, channel: c.channel, color: c.color ?? TIER_COLORS[i % TIER_COLORS.length],
    recipients: c.recipients ?? 0, reg: c.reg ?? 0, conv: c.conv ?? null, attended: c.attended ?? 0,
    revenue: c.revenue ?? 0, cost: c.cost ?? null, roi: c.roi ?? null, cpr: c.cpr ?? null,
  })), [data]);
  const kp = (data as any)?.kpis ?? null;
  const bySource = ((data as any)?.bySource ?? []).map((s: any, i: number) => ({
    id: s.id, label: s.label, color: s.color ?? PALETTE[i % PALETTE.length], reg: s.reg ?? 0,
  }));

  const organic = byCampaign.find((c: any) => c.recipients === 0 && /organic|direct/i.test(c.channel));
  const paidRows = byCampaign.filter((c: any) => c.recipients > 0);
  const campReg = kp?.attributedRegistrations ?? 0;
  const campRev = kp?.attributedRevenue ?? 0;
  const paidReg = kp?.paidRegistrations ?? 0;
  const paidRev = kp?.paidRevenue ?? 0;
  const costAvailable = kp?.costAvailable ?? false;
  const totalCost = kp?.campaignSpend ?? null;
  const blendedRoi = kp?.blendedRoi ?? null;
  const cprKpi = kp?.costPerRegistration ?? null;
  const paidRecip = paidRows.reduce((acc: number, c: any) => acc + c.recipients, 0);
  const paidAtt = paidRows.reduce((acc: number, c: any) => acc + c.attended, 0);
  const sorted = [...byCampaign].sort((a: any, z: any) => ((z as any)[sortKey] || 0) - ((a as any)[sortKey] || 0));

  // ui-config — funnel VIEW SCAFFOLD: every `n` is reduced from real fetched
  // campaign rows; `pct: 100` is the definitional funnel top (recipients
  // reached = 100% baseline), not a fabricated metric. Marked per Claude Code
  // docket 2026-07-13 (seed-guard false-positive).
  const attrFunnel = [ // ui-config
    { id: "recipients", label: "Recipients reached", n: paidRecip, pct: 100, color: T.tDarkBlue },
    { id: "reg", label: "Registered (attributed)", n: paidReg, pct: paidRecip ? Math.round(paidReg / paidRecip * 100) : 0, color: T.p1 },
    { id: "att", label: "Attended", n: paidAtt, pct: paidRecip ? Math.round(paidAtt / paidRecip * 100) : 0, color: T.green1 },
  ];

  const exportRows = () => {
    const head = ["Campaign", "Channel", "Recipients", "Registrations", "Conversion %", "Attended", "Revenue", "Cost", "ROI", "Cost / reg"];
    const body = byCampaign.map((c: any) => [
      c.name, c.channel, c.recipients || "—", c.reg,
      c.conv != null ? (c.conv * 100).toFixed(1) : "—",
      c.attended, c.revenue, c.cost ?? "—",
      c.roi != null ? `${(c.roi * 100).toFixed(0)}%` : "—",
      c.cpr != null ? c.cpr.toFixed(2) : "—",
    ] as (string | number)[]);
    return [head, ...body];
  };

  const sortBtn = (key: "revenue" | "reg" | "roi", label: string) => (
    <Btn variant="secondary" onClick={() => setSortKey(key)} style={{
      height: "auto", padding: "3px 9px", borderRadius: 999, cursor: "pointer", fontFamily: T.font, fontSize: 11, fontWeight: 600,
      border: `1px solid ${sortKey === key ? T.brandFill : T.border}`, background: sortKey === key ? T.brandFill : T.white,
      color: sortKey === key ? T.onColor : T.n2, boxShadow: "none",
    }}>{label}</Btn>
  );

  return (
    <Screen rail="reports" tabs={reportTabs(nav, "campaigns")} activeTab="campaigns">
      <PageHeader
        title={t("reports.extra.byCampaign", "By campaign")}
        subtitle={i18nT("reports.extra.attributionRegistrationsRevenueDrivenByEachCampaignRangemetaLabelTolower.icu", { value0: rangeMeta.label.toLowerCase(), value1: locName })}
        breadcrumb={<span style={{ cursor: "pointer" }} onClick={() => nav.go("reports")}>{t("reports.extra.reports", "← Reports")}</span>}
        actions={<>
          <LocationSelect value={location} onChange={setLocation} locs={locs} />
          <RangeSelect value={range} onChange={(id) => { setRange(id); toast({ tone: "info", title: i18nT("reports.extra.rangeRangesFindRRIdIdRanges1Label.icu", { value0: (RANGES.find((r) => r.id === id) || RANGES[1]).label }), subtitle: t("reports.extra.attributionRecalculated", "Attribution recalculated.") }); }} />
          <ReportExport filename={`campaign-attribution-${range}`} rows={exportRows} />
        </>}
      />
      <div style={{ flex: 1, overflow: "auto", padding: "16px 24px 24px", display: "flex", flexDirection: "column", gap: 16 }}>
        <LoadErrorNote failed={failed} />
        <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 12 }}>
          <ScoreTile label={t("reports.extra.attributedRegistrations", "Attributed registrations")} value={kp ? fmtNum(campReg) : "—"} accent={T.p1} icon={<Icon.users size={14} />} sub={kp && campReg > 0 ? `${Math.round(paidReg / campReg * 100)}% from paid campaigns` : undefined} />
          <ScoreTile label={t("reports.extra.attributedRevenue", "Attributed revenue")} value={kp ? fmtMoney(campRev) : "—"} accent={T.tOrange} icon={<Icon.ticket size={14} />} sub={kp ? `${fmtMoney(paidRev)} from paid` : undefined} />
          <ScoreTile label={t("reports.extra.campaignSpend", "Campaign spend")} value={costAvailable && totalCost != null ? fmtMoney(totalCost) : "—"} accent={T.tDarkBlue} icon={<Icon.send size={14} />} sub={costAvailable ? `${kp?.paidCampaignCount ?? paidRows.length} paid campaigns` : "Cost tracking not connected"} />
          <ScoreTile label={t("reports.extra.blendedRoi", "Blended ROI")} value={blendedRoi != null ? `${Math.round(blendedRoi * 100)}%` : "—"} accent={blendedRoi != null && blendedRoi > 0 ? T.green1 : blendedRoi != null ? T.red1 : T.tPurple} icon={<Icon.chart size={14} />} sub={blendedRoi != null && totalCost != null ? `${fmtMoney(paidRev - totalCost)} net of spend` : "Requires cost tracking"} />
          <ScoreTile label={t("reports.extra.costRegistration", "Cost / registration")} value={cprKpi != null ? `$${cprKpi.toFixed(2)}` : "—"} accent={T.tPurple} icon={<Icon.refresh size={14} />} sub={t('reports.extra.sub')} />
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1.5fr 1fr", gap: 16 }}>
          <Card>
            <div style={{ padding: "12px 16px", borderBottom: `1px solid ${T.border}`, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
              <h3 style={{ ...tx.h3, fontWeight: 600 }}>{t("reports.extra.campaignPerformance", "Campaign performance")}</h3>
              <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                <span style={{ fontSize: 11, color: T.soft }}>{t("reports.extra.sort", "Sort")}</span>
                {sortBtn("revenue", "Revenue")}{sortBtn("reg", "Regs")}{sortBtn("roi", "ROI")}
              </div>
            </div>
            {/* ui-raw: hand-rolled table — Table primitive's bordered/radius wrapper, uniform
              * row-hover highlight and sort-icon slots would visibly diverge from this table's
              * conditional per-row border, dot+name composite cell, and conditional ROI coloring. */}
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: T.font, fontSize: 13 }}>
                <thead>
                  <tr style={{ background: T.n6, color: T.soft, fontSize: 11, textTransform: "uppercase", fontWeight: 600 }}>
                    <th style={thStyle}>{t("reports.extra.campaign", "Campaign")}</th>
                    <th style={{ ...thStyle, textAlign: "right" }}>{t("reports.extra.regs", "Regs")}</th>
                    <th style={{ ...thStyle, textAlign: "right" }}>{t("reports.extra.conv", "Conv")}</th>
                    <th style={{ ...thStyle, textAlign: "right" }}>{t("reports.extra.revenue", "Revenue")}</th>
                    <th style={{ ...thStyle, textAlign: "right" }}>{t("reports.extra.roi", "ROI")}</th>
                  </tr>
                </thead>
                <tbody>
                  {sorted.length === 0 ? (
                    <tr><td style={{ ...tdStyle, color: T.soft, fontSize: 12.5 }} colSpan={5}>{t("reports.extra.noCampaignActivityInThisPeriod", "No campaign activity in this period.")}</td></tr>
                  ) : sorted.map((c: any, i: number) => (
                    <tr key={c.id} style={{ borderTop: i ? `1px solid ${T.border}` : "none" }}>
                      <td style={tdStyle}>
                        <div style={{ display: "flex", gap: 9, alignItems: "center" }}>
                          <span style={{ width: 8, height: 8, borderRadius: 3, background: c.color, flexShrink: 0 }} />
                          <div>
                            <div style={{ color: T.n1, fontWeight: 600 }}>{c.name}</div>
                            <div style={{ color: T.soft, fontSize: 11 }}>{c.channel}{c.recipients ? /* TODO(i18n): replace template-literal with t('reports.extra.text2', { ... }) */` · ${fmtNum(c.recipients)} sent` : t('reports.extra.text3')}</div>
                          </div>
                        </div>
                      </td>
                      <td style={{ ...tdStyle, textAlign: "right", color: T.n1, fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>{fmtNum(c.reg)}</td>
                      <td style={{ ...tdStyle, textAlign: "right", color: T.n2, fontVariantNumeric: "tabular-nums" }}>{c.conv != null ? /* TODO(i18n): replace template-literal with t('reports.extra.text4', { ... }) */`${(c.conv * 100).toFixed(1)}%` : "—"}</td>
                      <td style={{ ...tdStyle, textAlign: "right", color: T.n1, fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>{fmtMoney(c.revenue)}</td>
                      <td style={{ ...tdStyle, textAlign: "right", fontVariantNumeric: "tabular-nums", fontWeight: 700, color: c.roi == null ? T.soft : c.roi > 0 ? T.green1 : T.red1 }}>{c.roi != null ? /* TODO(i18n): replace template-literal with t('reports.extra.text5', { ... }) */`${Math.round(c.roi * 100)}%` : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {organic ? (
              <div style={{ borderTop: `1px solid ${T.border}`, padding: "10px 16px", background: T.n6, fontSize: 11, color: T.soft }}>
                {t("reports.extra.organicDirectContributed", "Organic / direct contributed")}{" "}<b style={{ color: T.n1 }}>{fmtNum(organic.reg)}</b> {t("reports.extra.registrations", "registrations (")}{fmtMoney(organic.revenue)}{t("reports.extra.atNoMediaCost", ") at no media cost.")}</div>
            ) : null}
          </Card>

          <Card>
            <div style={{ padding: "12px 16px", borderBottom: `1px solid ${T.border}` }}>
              <h3 style={{ ...tx.h3, fontWeight: 600 }}>{t("reports.extra.paidCampaignFunnel", "Paid-campaign funnel")}</h3>
            </div>
            <div style={{ padding: 18 }}>
              {paidRecip === 0 ? (
                <div style={{ fontSize: 12.5, color: T.soft }}>{t("reports.extra.noPaidSendsInThisPeriod", "No paid sends in this period.")}</div>
              ) : (
                <>
                  <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                    {attrFunnel.map((s) => (
                      <div key={s.id}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 4 }}>
                          <span style={{ fontSize: 13, color: T.n1, fontWeight: 600 }}>{s.label}</span>
                          <span style={{ fontSize: 14, color: T.n1, fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>{fmtNum(s.n)}</span>
                        </div>
                        <div style={{ height: 22, background: T.n6, borderRadius: 5, overflow: "hidden" }}>
                          <div style={{ height: "100%", width: `${s.pct}%`, background: s.color, borderRadius: 5, transition: "width 240ms" }} />
                        </div>
                      </div>
                    ))}
                  </div>
                  <div style={{ marginTop: 14, padding: 12, background: T.p4, borderRadius: 8, fontSize: 12, color: T.n2, lineHeight: 1.5 }}>
                    {t("reports.extra.paidCampaignsReached", "Paid campaigns reached")}{" "}<b style={{ color: T.n1 }}>{fmtNum(paidRecip)}</b> {t("reports.extra.peopleAndConverted", "people and converted")}{" "}<b style={{ color: T.n1 }}>{(paidReg / paidRecip * 100).toFixed(1)}%</b> {t("reports.extra.toRegistrationOfWhom", "to registration, of whom")}{" "}<b style={{ color: T.n1 }}>{paidReg ? Math.round(paidAtt / paidReg * 100) : 0}%</b> {t("reports.extra.attended43573c3f", "attended.")}</div>
                </>
              )}
            </div>
          </Card>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
          <Card>
            <div style={{ padding: "12px 16px", borderBottom: `1px solid ${T.border}` }}>
              <h3 style={{ ...tx.h3, fontWeight: 600 }}>{t("reports.extra.revenueByCampaign", "Revenue by campaign")}</h3>
            </div>
            <BreakdownBars rows={byCampaign.map((c: any) => ({ id: c.id, label: c.name, color: c.color, revenue: c.revenue }))} valueKey="revenue" money />
          </Card>
          <Card>
            <div style={{ padding: "12px 16px", borderBottom: `1px solid ${T.border}` }}>
              <h3 style={{ ...tx.h3, fontWeight: 600 }}>{t("reports.extra.registrationsBySource", "Registrations by source")}</h3>
            </div>
            <BreakdownBars rows={bySource} valueKey="reg"
              footer={<><span>{bySource.length} {t("reports.extra.sources", "sources")}</span><span>{bySource.reduce((acc: number, x: any) => acc + x.reg, 0).toLocaleString()} {t("reports.extra.total", "total")}</span></>} />
          </Card>
        </div>
      </div>
    </Screen>
  );
}

/* ═══ Revenue ════════════════════════════════════════════════════════ */
export function ReportsRevenue() {
  const t = useT();
  const nav = useNav();
  const [range, setRange] = useState("12m");
  const [location, setLocation] = useState("all");
  const { data, failed, locs } = useMoneyReport(getReportRevenue, range, location);
  const pacing = useGoalPacing(range);
  const rangeMeta = RANGES.find((r) => r.id === range) ?? RANGES[4];
  const locName = location === "all" ? null : (locs.find((l) => l.id === location)?.name ?? null);

  const sum = (data as any)?.summary ?? null;
  const bd = (data as any)?.breakdown ?? null;
  const cmp = (data as any)?.comparison ?? null;
  const promo = (data as any)?.promoImpact ?? null;
  const provider = (data as any)?.providerStatus ?? null;

  const gross = m$(sum?.grossRevenueMinor);
  const net = m$(sum?.netRevenueMinor);
  const refunds = m$(sum?.refundedRevenueMinor);
  const fees = m$(bd?.feesMinor);
  const feesTracked = bd?.feesTracked ?? false;
  const dGross = sum && cmp?.grossRevenueMinor != null ? delta(gross, m$(cmp.grossRevenueMinor)) : undefined;
  const dNet = sum && cmp?.netRevenueMinor != null ? delta(net, m$(cmp.netRevenueMinor)) : undefined;
  const dRefund = sum && cmp?.refundedRevenueMinor != null ? delta(refunds, m$(cmp.refundedRevenueMinor), true) : undefined;

  // Real revenue goal — getReportGoals().pacing metric "revenue" (minor units).
  const revPace = pacing.find((x: any) => x.metric === "revenue") ?? null;
  const revTarget = revPace ? m$(revPace.target) : null;
  const projected = revPace?.projected != null ? m$(revPace.projected) : null;
  const paceToGoal = revTarget ? gross / revTarget : null;

  const series = ((data as any)?.buckets ?? []).map((bk: any) => ({ label: bk.label ?? bk.bucket, rev: m$(bk.grossRevenueMinor) }));
  const byTicket = ((data as any)?.ticketTypes ?? []).map((t: any, i: number) => ({ id: t.id ?? t.ticketName, label: t.label ?? t.ticketName, color: t.color ?? TIER_COLORS[i % TIER_COLORS.length], revenue: m$(t.grossRevenueMinor) }));
  const byLoc = ((data as any)?.byLocation?.rows ?? []).map((l: any, i: number) => ({ id: l.id ?? l.locationName ?? String(i), label: l.locationName ?? l.label ?? "Location", color: T.tDarkBlue, revenue: m$(l.grossMinor) }));
  const byCamp = ((data as any)?.bySource?.rows ?? []).map((s2: any, i: number) => ({ id: s2.id ?? s2.source ?? String(i), label: s2.label ?? s2.source ?? "Source", color: PALETTE[i % PALETTE.length], revenue: m$(s2.grossMinor) }));

  const exportRows = () => {
    const rows: (string | number)[][] = [
      ["Metric", "Value", "Goal", "Prior period"],
      ["Gross revenue", gross, revTarget ?? "—", cmp?.grossRevenueMinor != null ? m$(cmp.grossRevenueMinor) : "—"],
      ["Processing fees", feesTracked ? fees : "—", "", ""],
      ["Refunds", refunds, "", cmp?.refundedRevenueMinor != null ? m$(cmp.refundedRevenueMinor) : "—"],
      ["Net revenue", net, "", cmp?.netRevenueMinor != null ? m$(cmp.netRevenueMinor) : "—"],
      ["", "", "", ""], ["Ticket type", "Revenue", "", ""]];
    byTicket.forEach((t: any) => rows.push([t.label, t.revenue, "", ""]));
    rows.push(["", "", "", ""], ["Location", "Revenue", "", ""]);
    byLoc.forEach((l: any) => rows.push([l.label, l.revenue, "", ""]));
    return rows;
  };

  return (
    <Screen rail="reports" tabs={reportTabs(nav, "revenue")} activeTab="revenue">
      <PageHeader
        title={t("reports.extra.revenue", "Revenue")}
        subtitle={t("reports.extra.grossNetByTierLocationCampaignRangemetaLabelTolowercaseLocnameSumCurrenc.icu", { range, locationScope: locName ? "named" : "all", locationName: locName ?? undefined, currency: sum?.currency ?? "USD", providerState: provider ? "present" : "absent", provider: provider ? String(provider.label) : undefined })}
        breadcrumb={<span style={{ cursor: "pointer" }} onClick={() => nav.go("reports")}>{t("reports.extra.reports", "← Reports")}</span>}
        actions={<>
          <LocationSelect value={location} onChange={setLocation} locs={locs} />
          <RangeSelect value={range} onChange={(id) => { setRange(id); toast({ tone: "info", title: i18nT("reports.extra.rangeRangesFindRRIdIdRanges4Label.icu", { value0: (RANGES.find((r) => r.id === id) || RANGES[4]).label }), subtitle: t("reports.extra.revenueFiguresUpdated", "Revenue figures updated.") }); }} />
          <ReportExport filename={`revenue-report-${range}`} rows={exportRows} />
        </>}
      />
      <div style={{ flex: 1, overflow: "auto", padding: "16px 24px 24px", display: "flex", flexDirection: "column", gap: 16 }}>
        <LoadErrorNote failed={failed} />
        <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 12 }}>
          <ScoreTile label={t("reports.extra.grossRevenue", "Gross revenue")} value={sum ? fmtMoney(gross) : "—"} delta={dGross} accent={T.tOrange} icon={<Icon.ticket size={14} />} goal={revTarget != null ? { actual: gross, target: revTarget } : undefined} />
          <ScoreTile label={t("reports.extra.netRevenue", "Net revenue")} value={sum ? fmtMoney(net) : "—"} delta={dNet} accent={T.green1} icon={<Icon.chart size={14} />} sub={sum ? `${fmtPct(net / Math.max(1, gross), 0)} of gross` : undefined} />
          <ScoreTile label={t("reports.extra.processingFees", "Processing fees")} value={feesTracked ? fmtMoney(fees) : "—"} accent={T.tDarkBlue} icon={<Icon.refresh size={14} />} sub={feesTracked ? `${fmtPct(fees / Math.max(1, gross), 1)} of gross` : "Not tracked by provider"} />
          <ScoreTile label={t("reports.extra.refunds", "Refunds")} value={sum ? fmtMoney(refunds) : "—"} delta={dRefund} accent={T.red1} icon={<Icon.warn size={14} />} sub={sum ? `${fmtPct(refunds / Math.max(1, gross), 1)} of gross` : undefined} />
          <ScoreTile label={t("reports.extra.promoDiscounts", "Promo / discounts")} value={promo?.promoTracked ? fmtMoney(m$(promo.totalDiscountMinor)) : "—"} accent={T.tPurple} icon={<Icon.tags size={14} />} sub={promo?.promoTracked && promo.discountRate != null ? `${fmtPct(promo.discountRate, 1)} given away` : "Promo tracking not connected"} />
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1.5fr 1fr", gap: 16 }}>
          <Card style={{ padding: 20 }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 12 }}>
              <h3 style={{ ...tx.h3, fontWeight: 600 }}>{t("reports.extra.revenueTrend", "Revenue trend")}</h3>
              <span style={{ fontSize: 12, color: T.soft }}>{fmtMoney(gross)} {t("reports.extra.gross", "gross ·")}{fmtMoney(net)} {t("reports.extra.net", "net")}</span>
            </div>
            <div style={{ position: "relative", height: 160, display: "flex", alignItems: "flex-end", gap: 4 }}>
              {series.map((s2: any, i: number) => {
                const maxRev = Math.max(1, ...series.map((x: any) => x.rev));
                return (
                  <div key={i} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "stretch" }}>
                    <div title={i18nT("reports.extra.s2LabelFmtmoneyfullS2Rev.icu", { value0: s2.label, value1: fmtMoneyFull(s2.rev) })} style={{ background: T.tOrange, borderRadius: "3px 3px 0 0", height: `${Math.max(4, (s2.rev / maxRev) * 150)}px`, opacity: 0.9 }} />
                    <div style={{ fontSize: 10, color: T.soft, textAlign: "center", marginTop: 3 }}>{s2.label}</div>
                  </div>
                );
              })}
              {series.length === 0 ? <div style={{ fontSize: 12.5, color: T.soft }}>{t("reports.extra.noDataForThisPeriod", "No data for this period.")}</div> : null}
            </div>
          </Card>
          <Card>
            <div style={{ padding: "12px 16px", borderBottom: `1px solid ${T.border}` }}>
              <h3 style={{ ...tx.h3, fontWeight: 600 }}>{t("reports.extra.pacingToGoal", "Pacing to goal")}</h3>
            </div>
            {revPace == null ? (
              <div style={{ padding: 18, fontSize: 12.5, color: T.soft }}>{t("reports.extra.noRevenueGoalSetForThisPeriod", "No revenue goal set for this period.")}</div>
            ) : (
              <div style={{ padding: 18, display: "flex", flexDirection: "column", gap: 14 }}>
                <div>
                  <div style={{ fontSize: 28, fontWeight: 700, color: T.n1, letterSpacing: -0.5 }}>{fmtMoney(gross)}</div>
                  <div style={{ fontSize: 12, color: T.soft }}>{t("reports.extra.of", "of")}{" "}{fmtMoney(revTarget ?? 0)} {t("reports.extra.target", "target ·")}{rangeMeta.label.toLowerCase()}</div>
                </div>
                <div style={{ height: 8, background: T.n5, borderRadius: 999, overflow: "hidden" }}>
                  <div style={{ height: "100%", width: `${Math.min(100, (paceToGoal ?? 0) * 100)}%`, background: revPace.aheadOfGoal ? T.green1 : T.p1, borderRadius: 999 }} />
                </div>
                <div style={{ padding: 12, background: T.n6, borderRadius: 8, fontSize: 12, color: T.n2, lineHeight: 1.5 }}>
                  {projected != null ? (<>{t("reports.extra.atTheCurrentPaceThisPeriodProjectsTo", "At the current pace this period projects to")}{" "}<b style={{ color: T.n1 }}>{fmtMoney(projected)}</b> {t("reports.extra.gross82c11df6", "gross —")}</>) : null}
                  {revPace.aheadOfGoal
                    ? <span style={{ color: T.green1, fontWeight: 700 }}>{t("reports.extra.aheadOfGoal", "ahead of goal")}</span>
                    : <span style={{ color: T.orange1, fontWeight: 700 }}>{Math.max(0, Math.round((1 - (paceToGoal ?? 0)) * 100))}{t("reports.extra.shortOfGoal", "% short of goal")}</span>}.
                </div>
              </div>
            )}
          </Card>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16 }}>
          <Card>
            <div style={{ padding: "12px 16px", borderBottom: `1px solid ${T.border}` }}>
              <h3 style={{ ...tx.h3, fontWeight: 600 }}>{t("reports.extra.byTicketType", "By ticket type")}</h3>
            </div>
            <BreakdownBars rows={byTicket} valueKey="revenue" money footer={<><span>{t("reports.extra.paidTiersOnly", "Paid tiers only")}</span><span>{fmtMoney(gross)}</span></>} />
          </Card>
          <Card>
            <div style={{ padding: "12px 16px", borderBottom: `1px solid ${T.border}` }}>
              <h3 style={{ ...tx.h3, fontWeight: 600 }}>{t("reports.extra.byLocation", "By location")}</h3>
            </div>
            <BreakdownBars rows={byLoc} valueKey="revenue" money />
          </Card>
          <Card>
            <div style={{ padding: "12px 16px", borderBottom: `1px solid ${T.border}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <h3 style={{ ...tx.h3, fontWeight: 600 }}>{t("reports.extra.bySource", "By source")}</h3>
              <Btn variant="link" size="sm" onClick={() => nav.go("reports.campaigns")}>{t("reports.extra.detail", "Detail →")}</Btn>
            </div>
            <BreakdownBars rows={byCamp} valueKey="revenue" money />
          </Card>
        </div>
      </div>
    </Screen>
  );
}

/* ═══ By event — ranked performance vs the workspace benchmark (#805) ═══
 * Consumes getReportsByEvent (GET /v1/admin/reports/by-event): a ranked events
 * table + workspace benchmark tiles. Previously `reports.event` mapped to the
 * Overview with a hardcoded activeTab, so clicking "By event" changed the URL
 * but re-rendered the identical dashboard — the tab was dead. Typeof-guarded
 * like reports-fill's getOccupancyReport so an older client copy degrades to an
 * honest unavailable note — never fabricated rows. Money in minor units → /100. */
export function ReportsByEvent() {
  const t = useT();
  const nav = useNav();
  const [range, setRange] = useState("12m");
  const [sortKey, setSortKey] = useState<"registered" | "revenue" | "fill">("registered");
  const [data, setData] = useState<Record<string, any> | null>(null);
  const [failed, setFailed] = useState(false);
  const rangeMeta = RANGES.find((r) => r.id === range) ?? RANGES[4];

  useEffect(() => {
    let live = true;
    setFailed(false);
    const fn = (apiClient as Record<string, unknown>).getReportsByEvent;
    if (typeof fn !== "function") { setData(null); setFailed(true); return; }
    (fn as (p: Record<string, unknown>, o: unknown) => Promise<Record<string, unknown>>)({ range }, REPORTS_OVERVIEW_OPTS)
      .then((res) => { if (live) setData(res as Record<string, any>); })
      .catch(() => { if (live) { setData(null); setFailed(true); } });
    return () => { live = false; };
  }, [range]);

  // A fill/attRate may arrive as a 0..1 fraction or a 0..100 percentage — render both honestly.
  const pctOf = (f: unknown): number | null => { if (f == null) return null; const n = Number(f); if (!isFinite(n)) return null; return n > 1 ? Math.round(n) : Math.round(n * 100); };
  const rows = useMemo(() => ((data?.events ?? []) as Record<string, any>[]).map((e, i) => ({
    id: String(e.eventId ?? e.id ?? i),
    title: String(e.eventTitle ?? e.title ?? "Untitled event"),
    status: String(e.status ?? ""),
    startAt: (e.startAt ?? e.startsAt ?? null) as string | null,
    registered: Number(e.registered) || 0,
    attended: Number(e.attended) || 0,
    attRate: pctOf(e.attRate) ?? (Number(e.registered) > 0 ? Math.round(((Number(e.attended) || 0) / Number(e.registered)) * 100) : null),
    revenue: m$(typeof e.revenue === "number" ? e.revenue : e.revenueMinor),
    fill: pctOf(e.fill),
    capacity: Number(e.capacity) || 0,
    noShows: e.noShows != null ? Number(e.noShows) : null,
    settled: e.settled !== false,
    hue: String(e.hue ?? "") || TIER_COLORS[0],
  })), [data]);
  const sorted = useMemo(() => [...rows].sort((a, z) => ((z[sortKey] ?? 0) as number) - ((a[sortKey] ?? 0) as number)), [rows, sortKey]);
  const bm = (data?.benchmark ?? null) as Record<string, any> | null;
  const bmRevenue = bm ? m$(typeof bm.revenue === "number" ? bm.revenue : bm.revenueMinor) : null;
  const bmFill = bm ? pctOf(bm.fill) : null;
  const bmAtt = bm ? pctOf(bm.attRate) : null;
  const bmSub = bm?.settledEventCount ? `Workspace benchmark · ${bm.settledEventCount} settled event${bm.settledEventCount === 1 ? "" : "s"}` : "Workspace benchmark";
  const vsBm = (v: number | null, ref: number | null): { color: string; label: string } | null => {
    if (v == null || ref == null || ref === 0) return null;
    const up = v >= ref;
    return { color: up ? T.green1 : T.red1, label: t("reports.extra.upVsBenchmark.icu", { direction: up ? "up" : "down" }) };
  };
  const statusTone = (s: string): "success" | "info" | "neutral" | "danger" => (
    s === "live" ? "success" : s === "scheduled" ? "info" : s === "cancelled" ? "danger" : "neutral");
  const statusLabel = (s: string): string => (s === "live" ? "Live" : s === "scheduled" ? "Upcoming" : s === "completed" ? "Past" : s === "archived" ? "Past" : s === "cancelled" ? "Cancelled" : s || "—");

  const exportRows = () => {
    const head = ["Rank", "Event", "Status", "Date", "Registered", "Attended", "Attendance %", "Fill %", "Revenue", "No-shows"];
    const body = sorted.map((r, i) => [
      i + 1, r.title, r.status, r.startAt ?? "—", r.registered, r.attended,
      r.attRate != null ? r.attRate : "—", r.fill != null ? r.fill : "—", r.revenue, r.noShows ?? "—",
    ] as (string | number)[]);
    return [head, ...body];
  };

  const sortBtn = (key: "registered" | "revenue" | "fill", label: string) => (
    <Btn variant="secondary" onClick={() => setSortKey(key)} style={{
      height: "auto", padding: "3px 9px", borderRadius: 999, cursor: "pointer", fontFamily: T.font, fontSize: 11, fontWeight: 600,
      border: `1px solid ${sortKey === key ? T.brandFill : T.border}`, background: sortKey === key ? T.brandFill : T.white,
      color: sortKey === key ? T.onColor : T.n2, boxShadow: "none",
    }}>{label}</Btn>
  );

  return (
    <Screen rail="reports" tabs={reportTabs(nav, "events")} activeTab="events">
      <PageHeader
        title={t("reports.extra.byEvent", "By event")}
        subtitle={i18nT("reports.extra.everyEventRankedAgainstTheWorkspaceBenchmarkRangemetaLabelTolowercase.icu", { value0: rangeMeta.label.toLowerCase() })}
        breadcrumb={<span style={{ cursor: "pointer" }} onClick={() => nav.go("reports")}>{t("reports.extra.reports", "← Reports")}</span>}
        actions={<>
          <RangeSelect value={range} onChange={(id) => { setRange(id); toast({ tone: "info", title: i18nT("reports.extra.rangeRangesFindRRIdIdRanges4Label.icu", { value0: (RANGES.find((r) => r.id === id) || RANGES[4]).label }), subtitle: t("reports.extra.eventRankingUpdated", "Event ranking updated.") }); }} />
          <ReportExport filename={`by-event-${range}`} rows={exportRows} />
        </>}
      />
      <div style={{ flex: 1, overflow: "auto", padding: "16px 24px 24px", display: "flex", flexDirection: "column", gap: 16 }}>
        <LoadErrorNote failed={failed} />
        {/* Workspace benchmark tiles — the bar every event is measured against.
          * flexShrink: 0 on the scroll-region children (2026-07-10 PM fix,
          * ingested 2026-07-13): keeps natural height so the region scrolls
          * instead of compressing the tiles/table. */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12, flexShrink: 0 }}>
          <ScoreTile label={t("reports.extra.registeredEvent", "Registered / event")} value={bm ? fmtNum(Number(bm.registered) || 0) : "—"} accent={T.p1} icon={<Icon.users size={14} />} sub={bmSub} />
          <ScoreTile label={t("reports.extra.attendanceRate", "Attendance rate")} value={bmAtt != null ? `${bmAtt}%` : "—"} accent={T.green1} icon={<Icon.check size={14} />} sub={bmSub} />
          <ScoreTile label={t("reports.extra.revenueEvent", "Revenue / event")} value={bmRevenue != null ? fmtMoney(bmRevenue) : "—"} accent={T.tOrange} icon={<Icon.ticket size={14} />} sub={bmSub} />
          <ScoreTile label={t("reports.extra.fillRate", "Fill rate")} value={bmFill != null ? `${bmFill}%` : "—"} accent={T.tPurple} icon={<Icon.chart size={14} />} sub={bmSub} />
        </div>

        {/* Ranked events table */}
        <Card style={{ flexShrink: 0 }}>
          <div style={{ padding: "12px 16px", borderBottom: `1px solid ${T.border}`, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
            <h3 style={{ ...tx.h3, fontWeight: 600 }}>{t("reports.extra.rankedEvents", "Ranked events")}</h3>
            <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
              <span style={{ fontSize: 11, color: T.soft }}>{t("reports.extra.rankBy", "Rank by")}</span>
              {sortBtn("registered", "Registrations")}{sortBtn("revenue", "Revenue")}{sortBtn("fill", "Fill")}
            </div>
          </div>
          {/* ui-raw: hand-rolled table — Table primitive's bordered/radius wrapper, uniform
            * row-hover highlight and sort-icon slots would visibly diverge from this table's
            * conditional per-row border, rank+pill composite cell, and vs-benchmark cell coloring. */}
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: T.font, fontSize: 13 }}>
              <thead>
                <tr style={{ background: T.n6, color: T.soft, fontSize: 11, textTransform: "uppercase", fontWeight: 600 }}>
                  <th style={{ ...thStyle, width: 44 }}>#</th>
                  <th style={thStyle}>{t("reports.extra.event", "Event")}</th>
                  <th style={{ ...thStyle, textAlign: "right" }}>{t("reports.extra.registered", "Registered")}</th>
                  <th style={{ ...thStyle, textAlign: "right" }}>{t("reports.extra.attended", "Attended")}</th>
                  <th style={{ ...thStyle, textAlign: "right" }}>{t("reports.extra.fill", "Fill")}</th>
                  <th style={{ ...thStyle, textAlign: "right" }}>{t("reports.extra.revenue", "Revenue")}</th>
                </tr>
              </thead>
              <tbody>
                {sorted.length === 0 ? (
                  <tr><td style={{ ...tdStyle, color: T.soft, fontSize: 12.5 }} colSpan={6}>{failed ? t('reports.extra.text6') : t('reports.extra.text7')}</td></tr>
                ) : sorted.map((r, i) => {
                  const revCue = vsBm(r.revenue, bmRevenue);
                  const fillCue = vsBm(r.fill, bmFill);
                  return (
                    <tr key={r.id} style={{ borderTop: i ? `1px solid ${T.border}` : "none" }}>
                      <td style={{ ...tdStyle, color: T.soft, fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>{i + 1}</td>
                      <td style={tdStyle}>
                        <div style={{ display: "flex", gap: 9, alignItems: "center" }}>
                          <span style={{ width: 8, height: 8, borderRadius: 3, background: r.hue, flexShrink: 0 }} />
                          <div style={{ minWidth: 0 }}>
                            <div style={{ color: T.n1, fontWeight: 600, display: "flex", alignItems: "center", gap: 8 }}>
                              <span style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{r.title}</span>
                              <Pill tone={statusTone(r.status)}>{statusLabel(r.status)}</Pill>
                            </div>
                            <div style={{ color: T.soft, fontSize: 11, marginTop: 1 }}>{fmtDay(r.startAt)}{r.noShows != null ? /* TODO(i18n): replace template-literal with t('reports.extra.text8', { ... }) */` · ${fmtNum(r.noShows)} no-shows` : ""}{r.settled ? "" : t('reports.extra.text9')}</div>
                          </div>
                        </div>
                      </td>
                      <td style={{ ...tdStyle, textAlign: "right", color: T.n1, fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>{fmtNum(r.registered)}</td>
                      <td style={{ ...tdStyle, textAlign: "right", color: T.n2, fontVariantNumeric: "tabular-nums" }}>{fmtNum(r.attended)}{r.attRate != null ? <span style={{ color: T.soft }}> · {r.attRate}%</span> : null}</td>
                      <td style={{ ...tdStyle, textAlign: "right", fontVariantNumeric: "tabular-nums", fontWeight: 600, color: fillCue?.color ?? T.n2 }} title={fillCue?.label}>{r.fill != null ? /* TODO(i18n): replace template-literal with t('reports.extra.text10', { ... }) */`${r.fill}%` : "—"}</td>
                      <td style={{ ...tdStyle, textAlign: "right", fontVariantNumeric: "tabular-nums", fontWeight: 600, color: revCue?.color ?? T.n1 }} title={revCue?.label}>{fmtMoneyFull(r.revenue)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {bm ? (
            <div style={{ borderTop: `1px solid ${T.border}`, padding: "10px 16px", background: T.n6, fontSize: 11, color: T.soft }}>
              {i18nT("reports.extra.greenRedFillAmpRevenueCellsCompareEachEventAgainstTheWorkspace.rendered", "Green/red fill & revenue cells compare each event against the workspace benchmark (")}{bm.registered != null ? /* TODO(i18n): replace template-literal with t('reports.extra.text11', { ... }) */`${fmtNum(Number(bm.registered) || 0)} registered` : "—"}{bmFill != null ? t('reports.extra.text12', { bmFill }) : ""}{bmRevenue != null ? /* TODO(i18n): replace template-literal with t('reports.extra.text13', { ... }) */` · ${fmtMoney(bmRevenue)} revenue` : ""} {t("reports.extra.perSettledEvent", "per settled event).")}</div>
          ) : null}
        </Card>
      </div>
    </Screen>
  );
}
